/**
 * Configurações da Operadora — autoatendimento white-label (admin).
 *
 * O admin da operadora parametriza marca (nome, tagline, cor, logo) e dados
 * institucionais (razão social, CNPJ, endereço, telefone, registro ANS) sem
 * código e sem rebuild: grava em crm.configuracoes_sistema e o app inteiro
 * (frontend via BrandingContext; e-mails/IA no backend, em evolução) lê de lá.
 */
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Building2, Save, Upload, Palette, Package } from 'lucide-react';
import api from '../services/api';
import { useBranding, urlUpload } from '../context/BrandingContext';

const CARD = { background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', padding: 24, marginBottom: 16 };
const LABEL = { display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 };
const INPUT = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #dce1e4', fontSize: '0.9rem', fontFamily: 'inherit' };
const GRID2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

/* O botao e os estados depois dele (ADR 0011, Camada 2).
   Cada estado diz o que esta acontecendo AGORA e o que a pessoa pode fazer —
   "aguarde" sem prazo nem dono e o que faz alguem clicar de novo. */
function textoDesde(horas) {
  if (horas < 1) return `há ${Math.max(1, Math.round(horas * 60))} min`;
  return `há ${Math.floor(horas)} h`;
}

function Pedido({ pd, versao, pedindo, onPedir }) {
  if (!pd) return null;

  // Sem agente no host, o pedido ficaria pendente para sempre. Prometer um
  // botao que nao executa e pior do que nao ter botao.
  if (!pd.agente_configurado) {
    return (
      <div style={{ fontSize: '0.82rem', color: '#8a6d00', marginTop: 8 }}>
        Para aplicar, acione quem administra o servidor desta instância, pois
        ela ainda não tem o agente de atualização instalado.
      </div>
    );
  }

  const p = pd.pedido;
  const desta = p && p.versao === versao;

  if (desta && p.estado === 'pendente') {
    // Tres situacoes que pareciam a mesma "aguardando". A diferenca entre elas
    // e o contato do agente: ele grava um sinal de vida a cada consulta, entao
    // "nunca consultou" e FATO, nao deducao por cronometro. Um pedido parado ha
    // 3 h pode ser um agente de 6 h funcionando normalmente.
    const contato = pd.agente_ultimo_contato ? new Date(pd.agente_ultimo_contato) : null;
    const agora = pd.agora ? new Date(pd.agora) : new Date();   // relogio do servidor
    const horasSemContato = contato ? (agora - contato) / 3_600_000 : null;
    const esperandoH = Math.floor((agora - new Date(p.pedido_em)) / 3_600_000);

    if (!contato) {
      return (
        <div style={{ fontSize: '0.85rem', color: '#c0392b', marginTop: 10 }}>
          <b>Pedido registrado, mas nenhum agente consultou esta instância.</b> O
          botão só registra a intenção: quem aplica é o agente instalado no
          servidor. Verifique com quem administra o host se ele está instalado e rodando.
        </div>
      );
    }

    if (horasSemContato > 12) {
      return (
        <div style={{ fontSize: '0.85rem', color: '#c0392b', marginTop: 10 }}>
          <b>O agente não consulta há {Math.floor(horasSemContato)} h</b> e o pedido
          continua parado. Ele pode ter sido desligado no servidor.
        </div>
      );
    }

    return (
      <div style={{ fontSize: '0.85rem', color: '#8a6d00', marginTop: 10 }}>
        <b>Atualização pedida por {p.pedido_por}.</b> O agente aplica na próxima
        consulta (a última foi {textoDesde(horasSemContato)}).
        {esperandoH >= 1 && <> Esperando há {esperandoH} h.</>} Esta tela avisa quando terminar.
      </div>
    );
  }

  if (desta && p.estado === 'em_andamento') {
    return (
      <div style={{ fontSize: '0.85rem', color: '#8a6d00', marginTop: 10 }}>
        <b>Atualizando para {p.versao}…</b> O sistema pode ficar alguns minutos
        fora do ar. Se falhar, ele volta sozinho para a versão anterior.
      </div>
    );
  }

  if (p && p.estado === 'falhou') {
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: '0.85rem', color: '#c0392b' }}>
          <b>A atualização para {p.versao} falhou</b> e a instância voltou à versão anterior.
          {p.erro && <div style={{ fontSize: '0.8rem', marginTop: 4 }}>{p.erro}</div>}
        </div>
        <BotaoPedir versao={versao} pedindo={pedindo} onPedir={onPedir} rotulo="Tentar de novo" />
      </div>
    );
  }

  return <BotaoPedir versao={versao} pedindo={pedindo} onPedir={onPedir} />;
}

function BotaoPedir({ versao, pedindo, onPedir, rotulo }) {
  return (
    <button onClick={onPedir} disabled={pedindo}
      style={{ marginTop: 12, background: '#f39c12', border: 'none', borderRadius: 8,
               padding: '9px 16px', cursor: pedindo ? 'not-allowed' : 'pointer',
               fontSize: '0.88rem', color: '#fff', fontWeight: 700, opacity: pedindo ? 0.6 : 1 }}>
      {pedindo ? 'Registrando…' : (rotulo || `Atualizar para ${versao}`)}
    </button>
  );
}

// ADR 0011 (Camada 1): a instancia mostra em que versao esta e se ha uma nova.
// Antes so o /health sabia — e ninguem abre /health. Aplicar a atualizacao
// continua fora do app (Camada 2); aqui o admin so DESCOBRE que ela existe.
// Data "YYYY-MM-DD" -> "DD/MM/YYYY" sem passar por Date (evita pular um dia por fuso).
const dataBR = (s) => {
  if (!s) return '';
  const [a, m, d] = String(s).slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : s;
};

function SobreEstaInstancia() {
  const [v, setV] = useState(null);
  const [checando, setChecando] = useState(false);
  const [pd, setPd] = useState(null);        // { pedido, agente_configurado }
  const [pedindo, setPedindo] = useState(false);

  const lerPedido = async () => {
    try { setPd((await api.get('/atualizacao/pedido')).data); } catch { /* opcional */ }
  };

  const pedirAtualizacao = async () => {
    setPedindo(true);
    try {
      await api.post('/atualizacao/pedido');
      await lerPedido();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Não foi possível registrar o pedido.');
    } finally { setPedindo(false); }
  };

  const consultar = async (forcar = false) => {
    setChecando(true);
    try {
      const { data } = await api.get('/diagnostico/versao', { params: { forcar } });
      setV(data);
    } catch {
      /* best-effort: instancia sem saida pra internet e caso esperado */
    } finally {
      setChecando(false);
    }
  };

  useEffect(() => { consultar(false); lerPedido(); }, []);
  // Enquanto o agente nao responde, a tela reconsulta: o deploy troca o
  // container, e o 'concluido' chega depois que esta aba ja estava aberta.
  useEffect(() => {
    const p = pd?.pedido;
    if (!p || (p.estado !== 'pendente' && p.estado !== 'em_andamento')) return;
    const t = setInterval(() => { lerPedido(); consultar(false); }, 15000);
    return () => clearInterval(t);
  }, [pd]);

  if (!v) return null;

  const nova = v.ha_atualizacao;
  return (
    <div style={{ ...CARD, borderLeft: `4px solid ${nova ? '#f39c12' : '#20a490'}` }}>
      <h3 style={{ marginTop: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Package size={17} /> Sobre esta instância
      </h3>

      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div>
          <label style={LABEL}>Versão instalada</label>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2c3e50' }}>{v.instalada}</div>
        </div>
        {v.disponivel && (
          <div>
            <label style={LABEL}>Versão disponível</label>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: nova ? '#f39c12' : '#27ae60' }}>
              {v.disponivel}
            </div>
          </div>
        )}
      </div>

      {nova && (
        <div style={{ marginTop: 16, background: '#fff8e1', border: '1px solid #f39c12',
                      borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontWeight: 700, color: '#8a6d00', marginBottom: 6 }}>
            Atualização disponível{v.publicada_em ? ` (publicada em ${dataBR(v.publicada_em)})` : ''}
          </div>
          {v.nota && (
            <div style={{ fontSize: '0.88rem', color: '#6b5600', whiteSpace: 'pre-wrap' }}>{v.nota}</div>
          )}
          {/* ADR 0011 Camada 2: o botao nao atualiza nada — REGISTRA a intencao.
              O agente que roda no host le e executa o deploy.sh. Mesmo desenho
              do celular, onde o app tambem nao se atualiza sozinho. */}
          <Pedido pd={pd} versao={v.disponivel} pedindo={pedindo} onPedir={pedirAtualizacao} />
          {v.url && (
            <div style={{ fontSize: '0.82rem', color: '#8a6d00', marginTop: 8 }}>
              Detalhes: <a href={v.url} target="_blank" rel="noreferrer">nota da versão</a>.
            </div>
          )}
        </div>
      )}

      {!nova && v.disponivel && (
        <div style={{ marginTop: 12, fontSize: '0.85rem', color: '#27ae60' }}>
          Esta instância está na versão mais recente.
        </div>
      )}

      {!v.habilitado && (
        <div style={{ marginTop: 12, fontSize: '0.85rem', color: '#7f8c8d' }}>
          Verificação de novas versões desligada nesta instância.
        </div>
      )}

      <button
        onClick={() => consultar(true)}
        disabled={checando || !v.habilitado}
        style={{ marginTop: 14, background: 'transparent', border: '1px solid #dce1e4',
                 borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem',
                 color: '#7f8c8d', fontWeight: 600 }}
      >
        {checando ? 'Verificando…' : 'Verificar agora'}
      </button>
      {v.verificado_em && (
        <span style={{ fontSize: '0.78rem', color: '#95a5a6', marginLeft: 10 }}>
          Última consulta: {new Date(v.verificado_em).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
        </span>
      )}

      {Array.isArray(v.historico) && v.historico.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, color: '#2c3e50' }}>
            Histórico de versões ({v.historico.length}): o que mudou em cada uma
          </summary>
          <div style={{ marginTop: 10, maxHeight: 340, overflowY: 'auto', borderTop: '1px solid #eee' }}>
            {v.historico.map((h) => (
              <div key={h.versao} style={{ padding: '8px 2px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: '#2c3e50' }}>{h.versao}</span>
                  {h.publicada_em && <span style={{ fontSize: '0.78rem', color: '#95a5a6' }}>{dataBR(h.publicada_em)}</span>}
                  {h.versao === v.instalada && (
                    <span style={{ fontSize: '0.72rem', color: '#20a490', border: '1px solid #20a490', borderRadius: 4, padding: '0 5px' }}>instalada</span>
                  )}
                </div>
                {h.nota && <div style={{ fontSize: '0.85rem', color: '#5b6b74', whiteSpace: 'pre-wrap', marginTop: 2 }}>{h.nota}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export default function AdminOperadora() {
  const { branding, recarregar } = useBranding();
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setForm({ ...branding }); }, [branding]);

  if (!form) return <div style={{ padding: 32 }}>Carregando…</div>;

  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  async function salvar() {
    setSalvando(true);
    try {
      const payload = {
        nome: form.nome, nome_operadora: form.nome_operadora, tagline: form.tagline, cor_primaria: form.cor_primaria,
        razao_social: form.razao_social, cnpj: form.cnpj, endereco: form.endereco,
        telefone: form.telefone, registro_ans: form.registro_ans,
        email_from_name: form.email_from_name,
      };
      await api.put('/operadora/branding', payload);
      recarregar();
      toast.success('Configurações da operadora salvas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSalvando(false); }
  }

  async function enviarLogo(ev) {
    const arquivo = ev.target.files?.[0];
    if (!arquivo) return;
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    try {
      await api.post('/operadora/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      recarregar();
      toast.success('Logo atualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro no upload do logo');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div style={{ padding: 32, maxWidth: 860, margin: '0 auto' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#2c3e50', marginTop: 0 }}>
        <Building2 size={26} /> Configurações da Operadora
      </h1>
      <p style={{ color: '#7f8c8d', marginTop: -8, marginBottom: 24 }}>
        Marca e dados institucionais usados nas telas, e-mails e documentos. Vale na hora, sem reiniciar.
      </p>

      <SobreEstaInstancia />

      <div style={CARD}>
        <h3 style={{ marginTop: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Palette size={17} /> Marca
        </h3>
        <div style={GRID2}>
          <div>
            <label style={LABEL}>Nome do sistema</label>
            <input style={INPUT} value={form.nome || ''} onChange={set('nome')} placeholder="Ex: AURA" />
          </div>
          <div>
            <label style={LABEL}>Nome da operadora (curto)</label>
            <input style={INPUT} value={form.nome_operadora || ''} onChange={set('nome_operadora')} placeholder="Ex: nome da sua operadora (usado em pesquisas e rodapés)" />
          </div>
          <div>
            <label style={LABEL}>Tagline (slogan)</label>
            <input style={INPUT} value={form.tagline || ''} onChange={set('tagline')} placeholder="Ex: Transformar cuidado em saúde" />
          </div>
          <div>
            <label style={LABEL}>Cor primária</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={form.cor_primaria || '#20a490'}
                     onChange={set('cor_primaria')}
                     style={{ width: 46, height: 38, border: '1px solid #dce1e4', borderRadius: 8, padding: 2, cursor: 'pointer' }} />
              <input style={{ ...INPUT, width: 120, fontFamily: 'monospace' }} value={form.cor_primaria || ''}
                     onChange={set('cor_primaria')} placeholder="#20a490" />
            </div>
          </div>
          <div>
            <label style={LABEL}>Logo</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {form.logo_url
                ? <img src={urlUpload(form.logo_url)} alt="logo" style={{ height: 40, maxWidth: 160, objectFit: 'contain', border: '1px solid #eee', borderRadius: 6, padding: 4 }} />
                : <span style={{ fontSize: '0.82rem', color: '#aaa' }}>usando o logo padrão do build</span>}
              <button onClick={() => fileRef.current?.click()}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #dce1e4', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: '0.85rem' }}>
                <Upload size={14} /> Trocar logo
              </button>
              {form.logo_url && (
                <button onClick={async () => {
                          try { await api.delete('/operadora/logo'); recarregar(); toast.success('Logo padrão restaurado'); }
                          catch (e) { toast.error('Erro ao restaurar logo'); }
                        }}
                        style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: '0.8rem' }}>
                  usar logo padrão
                </button>
              )}
              <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" style={{ display: 'none' }} onChange={enviarLogo} />
            </div>
          </div>
        </div>
      </div>

      <div style={CARD}>
        <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Institucional (rodapés de e-mail e documentos)</h3>
        <div style={GRID2}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Razão social</label>
            <input style={INPUT} value={form.razao_social || ''} onChange={set('razao_social')} placeholder="Razão social completa da operadora" />
          </div>
          <div>
            <label style={LABEL}>CNPJ</label>
            <input style={INPUT} value={form.cnpj || ''} onChange={set('cnpj')} placeholder="00.000.000/0000-00" />
          </div>
          <div>
            <label style={LABEL}>Registro ANS</label>
            <input style={INPUT} value={form.registro_ans || ''} onChange={set('registro_ans')} placeholder="Ex: 41729-7" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Endereço</label>
            <input style={INPUT} value={form.endereco || ''} onChange={set('endereco')} placeholder="Rua, número, bairro, cidade – UF, CEP" />
          </div>
          <div>
            <label style={LABEL}>Telefone(s)</label>
            <input style={INPUT} value={form.telefone || ''} onChange={set('telefone')} placeholder="(00) 0000-0000" />
          </div>
          <div>
            <label style={LABEL}>Nome do remetente de e-mail</label>
            <input style={INPUT} value={form.email_from_name || ''} onChange={set('email_from_name')} placeholder="Ex: nome da operadora" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={salvar} disabled={salvando}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)',
                         border: 'none', borderRadius: 10, padding: '12px 22px', fontWeight: 600,
                         fontSize: '0.95rem', cursor: 'pointer', opacity: salvando ? 0.7 : 1 }}>
          <Save size={16} /> {salvando ? 'Salvando…' : 'Salvar configurações'}
        </button>
      </div>
    </div>
  );
}
